import React, { useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import { EmailSignatureBoundary } from '@/lib/emailSignatureBoundary';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import FontFamily from '@tiptap/extension-font-family';
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  List,
  ListOrdered,
  Link as LinkIcon,
  Image as ImageIcon,
  Type,
  Palette,
} from 'lucide-react';
import { 
  Tooltip, 
  TooltipContent, 
  TooltipProvider, 
  TooltipTrigger 
} from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import {
  rememberTipTapEmittedHtml,
  syncTipTapExternalContent,
} from '@/lib/tiptapControlledSync';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  placeholder?: string;
  id?: string;
  name?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  /** Preserve the non-visible signature boundary nodes used by EmailModal. */
  preserveEmailSignatureBoundaries?: boolean;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  className = '',
  placeholder = 'Inizia a digitare...',
  id,
  name,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  preserveEmailSignatureBoundaries = false,
}) => {
  const [linkUrl, setLinkUrl] = useState('');
  const [linkOpen, setLinkOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [imageOpen, setImageOpen] = useState(false);
  const lastEmittedHtmlRef = useRef<string | null>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ link: false }),
      Image,
      Link.configure({
        openOnClick: false,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      TextStyle,
      Color,
      FontFamily,
      ...(preserveEmailSignatureBoundaries ? [EmailSignatureBoundary] : []),
    ],
    content: value,
    // Avoid SSR/hydration remount races; also safer with React 18 + WebKit.
    immediatelyRender: false,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      rememberTipTapEmittedHtml(lastEmittedHtmlRef, html);
      onChange(html);
    },
    editorProps: {
      attributes: {
        ...(id ? { id } : {}),
        ...(name ? { 'data-field-name': name } : {}),
        ...(ariaDescribedBy ? { 'aria-describedby': ariaDescribedBy } : {}),
        ...(ariaInvalid !== undefined ? { 'aria-invalid': String(ariaInvalid) } : {}),
        role: 'textbox',
        'aria-multiline': 'true',
        class: `prose max-w-none focus:outline-none ${className}`,
        placeholder,
      },
    },
  });

  // Sync only when parent pushes an external value (signature, Martina draft, reset).
  // Never rebuild the doc from our own onUpdate — that freezes Safari composers.
  useEffect(() => {
    syncTipTapExternalContent(editor, value, lastEmittedHtmlRef);
  }, [editor, value]);

  const addLink = () => {
    if (!editor) return;
    if (linkUrl) {
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: linkUrl })
        .run();
    }
    setLinkUrl('');
    setLinkOpen(false);
  };

  const removeLink = () => {
    if (!editor) return;
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    setLinkOpen(false);
  };

  const addImage = () => {
    if (!editor) return;
    if (imageUrl) {
      editor.chain().focus().setImage({ src: imageUrl }).run();
    }
    setImageUrl('');
    setImageOpen(false);
  };

  const MenuButton = ({ 
    onClick, 
    active = false, 
    disabled = false, 
    tooltip, 
    children 
  }: { 
    onClick: () => void; 
    active?: boolean; 
    disabled?: boolean; 
    tooltip: string; 
    children: React.ReactNode 
  }) => (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onClick}
            disabled={disabled}
            className={`h-8 w-8 p-0 ${active ? 'bg-muted text-accent-foreground' : ''}`}
          >
            {children}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  if (!editor) {
    return <div className="p-4 border rounded-none min-h-[200px] animate-pulse bg-accent" />;
  }

  return (
    <div className={`rounded-none border ${className}`}>
      <div className="flex flex-wrap gap-1 p-1 border-b">
        <MenuButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive('bold')}
          tooltip="Grassetto"
        >
          <Bold className="h-4 w-4" />
        </MenuButton>
        
        <MenuButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive('italic')}
          tooltip="Corsivo"
        >
          <Italic className="h-4 w-4" />
        </MenuButton>
        
        <MenuButton
          onClick={() => editor.chain().focus().toggleStrike().run()}
          active={editor.isActive('strike')}
          tooltip="Barrato"
        >
          <Strikethrough className="h-4 w-4" />
        </MenuButton>
        
        <div className="h-4 mx-1 border-r border-muted" />
        
        <MenuButton
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          active={editor.isActive({ textAlign: 'left' })}
          tooltip="Allinea a sinistra"
        >
          <AlignLeft className="h-4 w-4" />
        </MenuButton>
        
        <MenuButton
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          active={editor.isActive({ textAlign: 'center' })}
          tooltip="Centra"
        >
          <AlignCenter className="h-4 w-4" />
        </MenuButton>
        
        <MenuButton
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          active={editor.isActive({ textAlign: 'right' })}
          tooltip="Allinea a destra"
        >
          <AlignRight className="h-4 w-4" />
        </MenuButton>
        
        <MenuButton
          onClick={() => editor.chain().focus().setTextAlign('justify').run()}
          active={editor.isActive({ textAlign: 'justify' })}
          tooltip="Giustifica"
        >
          <AlignJustify className="h-4 w-4" />
        </MenuButton>
        
        <div className="h-4 mx-1 border-r border-muted" />
        
        <MenuButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive('bulletList')}
          tooltip="Elenco puntato"
        >
          <List className="h-4 w-4" />
        </MenuButton>
        
        <MenuButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive('orderedList')}
          tooltip="Elenco numerato"
        >
          <ListOrdered className="h-4 w-4" />
        </MenuButton>
        
        <div className="h-4 mx-1 border-r border-muted" />
        
        <Popover open={linkOpen} onOpenChange={setLinkOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={`h-8 w-8 p-0 ${editor.isActive('link') ? 'bg-muted text-accent-foreground' : ''}`}
            >
              <LinkIcon className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64">
            <div className="space-y-2">
              <h4 className="font-medium">Inserisci link</h4>
              <Input
                type="url"
                placeholder="https://example.com"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                className="col-span-3 h-8"
              />
              <div className="flex justify-between">
                {editor.isActive('link') && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={removeLink}
                    className="mt-2"
                  >
                    Rimuovi
                  </Button>
                )}
                <Button
                  type="button"
                  size="sm"
                  onClick={addLink}
                  className="mt-2 ml-auto"
                >
                  Salva
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
        
        <Popover open={imageOpen} onOpenChange={setImageOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 p-0"
            >
              <ImageIcon className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64">
            <div className="space-y-2">
              <h4 className="font-medium">Inserisci immagine</h4>
              <Input
                type="url"
                placeholder="https://example.com/image.jpg"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                className="col-span-3 h-8"
              />
              <Button
                type="button"
                size="sm"
                onClick={addImage}
                className="mt-2 ml-auto"
              >
                Inserisci
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>
      
      <EditorContent editor={editor} className="p-3 min-h-[150px]" />
    </div>
  );
};

export default RichTextEditor;
